/* @jsx h */
import { fetchCustomerAddresses } from "@app/api/client/index";
import { useMenu } from "@app/vue/providers/index";
import {
  CustomerAddressCard,
  CustomerAddressManagerForm,
} from "@app/vue/pages/ShopUser/subpages/CustomerAddressManager";
import { CustomerProfileAddress } from "@lemmonhq/types";
import { defineComponent, ref, h, onMounted, watch } from "vue";
import { getDeliveryCities } from "@lemmonhq/blogic";
import { useLocalization } from "@lemmonhq/uikit";

export const DeliverySection = defineComponent({
  props: {
    show: Boolean,
  },
  setup(props) {
    const { cart, shop } = useMenu();
    const { t } = useLocalization();
    const addresses = ref<CustomerProfileAddress[]>([]);
    const selectedAddressId = ref(cart.cart.addressId || "");
    const showAddForm = ref(false);
    const isLoading = ref(true);
    const deliveryCities = getDeliveryCities(shop.value.settings || []);

    // Watch for address selection changes
    watch(selectedAddressId, (newAddressId) => {
      cart.cart.addressId = newAddressId;
      // Also set the selectedAddress object for delivery fee calculation
      const selectedAddress = addresses.value.find(
        (addr) => addr.id === newAddressId,
      );
      cart.cart.selectedAddress = selectedAddress;
    });

    const isAddressValid = (address: CustomerProfileAddress) => {
      return deliveryCities.some(
        (city) => city.name.toLowerCase() === address.city.toLowerCase(),
      );
    };

    const loadAddresses = async () => {
      try {
        isLoading.value = true;
        const data = await fetchCustomerAddresses();
        console.log("DeliverySection - Loaded addresses:", data);
        console.log("DeliverySection - Delivery cities:", deliveryCities);

        // Auto-select first valid address if none selected
        if (data.length > 0 && !selectedAddressId.value) {
          const validAddress = data.find(isAddressValid);
          console.log("DeliverySection - Found valid address:", validAddress);
          if (validAddress) {
            selectedAddressId.value = validAddress.id;
            cart.cart.selectedAddress = validAddress;
          }
        }

        addresses.value = data;
      } catch (err) {
        console.error("Error loading addresses:", err);
      } finally {
        isLoading.value = false;
      }
    };

    const handleAddressSelect = (addressId: string) => {
      selectedAddressId.value = addressId;
      // The watch above will handle setting cart.cart.selectedAddress
    };

    const handleAddressAdded = async () => {
      await loadAddresses();
      // Auto-select the newly added address (it will be the last one)
      const newAddress = addresses.value[addresses.value.length - 1];
      if (newAddress && isAddressValid(newAddress)) {
        selectedAddressId.value = newAddress.id;
        cart.cart.selectedAddress = newAddress;
      }
      showAddForm.value = false;
    };

    onMounted(() => {
      if (props.show) {
        loadAddresses();
      }
    });

    watch(
      () => props.show,
      (show) => {
        if (show) {
          loadAddresses();
        }
      },
    );

    return () => {
      // Separate valid and invalid addresses (computed reactively)
      const validAddresses = addresses.value.filter(isAddressValid);
      const invalidAddresses = addresses.value.filter(
        (addr) => !isAddressValid(addr),
      );

      console.log(
        "DeliverySection - Render - Total addresses:",
        addresses.value.length,
      );
      console.log(
        "DeliverySection - Render - Valid addresses:",
        validAddresses.length,
      );
      console.log(
        "DeliverySection - Render - Invalid addresses:",
        invalidAddresses.length,
      );

      return (
        <div class={`p-2 ${!props.show ? "hidden" : ""}`}>
          <h3 class="text-lg font-semibold mb-4">
            {t("customer.checkout.delivery_details")}
          </h3>

          {showAddForm.value && (
            <CustomerAddressManagerForm
              onClose={() => (showAddForm.value = false)}
              onSuccess={handleAddressAdded}
              deliveryCities={deliveryCities}
            />
          )}

          {isLoading.value ? (
            <div class="flex justify-center items-center py-8">
              <div class="loading loading-spinner loading-lg"></div>
            </div>
          ) : (
            <div class="space-y-4">
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-500">
                  {addresses.value.length > 0
                    ? t("customer.checkout.select_delivery_address")
                    : ""}
                </span>
                <button
                  class="btn btn-primary btn-sm"
                  onClick={() => (showAddForm.value = true)}
                >
                  <span class="material-symbols-outlined mr-1">add</span>
                  {t("customer.checkout.add_address")}
                </button>
              </div>

              {addresses.value.length === 0 ? (
                <div class="text-center py-8">
                  <div class="mb-4">
                    <span class="material-symbols-outlined text-6xl text-gray-400">
                      location_on
                    </span>
                  </div>
                  <h4 class="text-lg font-semibold mb-2">
                    {t("customer.checkout.no_addresses_saved")}
                  </h4>
                  <p class="text-gray-500 mb-4">
                    {t("customer.checkout.add_first_address")}
                  </p>
                </div>
              ) : (
                <div class="space-y-3">
                  {/* Valid addresses first */}
                  {validAddresses.map((address) => (
                    <CustomerAddressCard
                      key={address.id}
                      address={address}
                      showRadio={true}
                      selected={address.id === selectedAddressId.value}
                      onClick={() => handleAddressSelect(address.id)}
                      onDelete={() => loadAddresses()}
                    />
                  ))}

                  {/* Invalid addresses at bottom, faded */}
                  {invalidAddresses.length > 0 && (
                    <div class="space-y-3 pt-4 border-t border-gray-200">
                      <p class="text-sm text-gray-500">
                        {t("customer.checkout.addresses_not_available")}
                      </p>
                      {invalidAddresses.map((address) => (
                        <CustomerAddressCard
                          key={address.id}
                          address={address}
                          showRadio={true}
                          selected={false}
                          disabled={true}
                          onDelete={() => loadAddresses()}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      );
    };
  },
});
